import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';

import { PencilIcon, colors } from '../../assets';
import { HelperTxt } from '../helperTxt/HelperTxt';
import { TypographyInput } from '../typographyInput/TypographyInput';
import { TypographyInputProps } from '../typographyInput/TypographyInput.types';
import { useFormTypographyInputStyles } from './FormItems.styles';
import { FormItem } from './FormItems.types';

export const FormTypographyInput: FC<FormItem & TypographyInputProps> = memo(
  ({ control, className, name, required, ...rest }) => {
    const {
      field: { onBlur, ...field },
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue: undefined,
    });

    const helperText = useMemo(() => {
      if (error) {
        return error.message;
      }

      if (required) {
        return '*required';
      }

      return '';
    }, [error, required]);

    const ref = useRef<HTMLInputElement | null>();
    const [editMode, setEditMode] = useState(false);

    const editModeOn = useCallback(() => {
      setEditMode(true);

      setTimeout(() => {
        if (ref.current) {
          ref.current.focus();
        }
      }, 0);
    }, []);

    const editModeOff = useCallback(() => {
      setEditMode(false);
    }, []);

    const styles = useFormTypographyInputStyles({ editMode });

    return (
      <FormControl error={!!error} fullWidth className={className}>
        <div className={styles.wrapper}>
          <TypographyInput
            {...rest}
            {...field}
            inputRef={ref}
            required={required}
            error={!!error}
            autoFocus
            onBlur={() => {
              editModeOff();
              onBlur();
            }}
          />
        </div>

        <div className={styles.wrapper} style={{ wordBreak: 'break-word' }}>
          <Typography variant="h4" component="span">
            {field.value || <div style={{ opacity: 0.5 }}>{rest.placeholder}</div>}
          </Typography>

          <div className={styles.iconButton}>
            <IconButton onClick={editModeOn} disabled={rest.disabled}>
              <PencilIcon color={rest.disabled ? colors.grey60 : colors.primary.main} fontSize={18} />
            </IconButton>
          </div>
        </div>

        {helperText && <HelperTxt>{helperText}</HelperTxt>}
      </FormControl>
    );
  },
);
